import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';

import { settingsStore } from '@/stores/settings.store';

export const formatDate = (
  dateStr: string | Date,
  newFormat: string = 'MMM d'
) => {
  try {
    // If tz is not specified, it will be parsed as local time
    // For example, "2024-08-01" will be parsed into a Date representing Aug 1 12am local time
    // If then newFormat is "yyyy-MM-dd'T'HH:mmX" and if for example local time is UTC-04:00, it
    // will be formatted as "2024-08-01T00:00:00.000-04:00"
    if (dateStr instanceof Date) {
      dateStr = dateStr.toISOString();
    }
    return format(parseISO(dateStr), newFormat);
  } catch (_e) {
    if (dateStr instanceof Date) {
      return dateStr.toISOString();
    }
    return dateStr;
  }
};

/**
 * Aligns the date to output a human readable format
 * nearest to the best possible format
 * */
export function formatDateHumanReadable(d?: Date | string) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  if (Date.now() - date.getTime() <= 500) return 'just now';
  return format(date, 'MMM d');
}

/**
 * Formats date as "Fri, Jun 27"
 * */
export function formatDateWithDay(d?: Date | string) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return format(date, 'EEE, MMM d');
}

/**
 * Formats time respecting user's 12hr/24hr preference.
 *
 * Reads preference synchronously from settingsStore. Does NOT subscribe
 * to changes. React components should use the `<Time>` component or
 * wrap with `observer` to re-render when preference changes.
 *
 * @example
 * // Or use the Time component directly
 * return <Time date={date} />;
 *
 * // In non-React code (uses current preference)
 * const timeStr = formatTime(date);
 */
export function formatTime(
  d?: Date | string,
  { hourOnly = false }: { hourOnly?: boolean } = {}
): string {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';

  const options: Intl.DateTimeFormatOptions = hourOnly
    ? { hour: 'numeric' }
    : { hour: 'numeric', minute: '2-digit' };

  // null = use browser/OS locale default (omit hour12 option)
  const prefer12Hr = settingsStore.timeFormat;
  if (prefer12Hr !== null) {
    options.hour12 = prefer12Hr;
  }
  // When null, Intl.DateTimeFormat uses the locale's natural default

  return date.toLocaleTimeString(undefined, options);
}

/**
 * The hour and minute options for a 12hr/24hr preference, for callers that
 * format with their own locale call or add date parts. `null` leaves the
 * clock to the browser/OS locale default.
 */
export function timeFormatOptions(
  prefer12Hr: boolean | null
): Intl.DateTimeFormatOptions {
  const options: Intl.DateTimeFormatOptions = {
    // Some locales (en-GB) drop a 24-hour clock's leading zero with 'numeric'
    hour: prefer12Hr === false ? '2-digit' : 'numeric',
    minute: '2-digit',
  };
  if (prefer12Hr !== null) {
    options.hour12 = prefer12Hr;
  }
  return options;
}

/**
 * Converts an HH:mm string (24hr) to locale-aware display format.
 * Use this to display stored time values in the user's preferred format.
 *
 * @example
 * formatTimeFromHHmm("15:30") // "3:30 PM" in 12hr, "15:30" in 24hr
 */
export function formatTimeFromHHmm(hhmm: string): string {
  if (!hhmm) return '';
  const [hours, minutes] = hhmm.split(':');
  const date = new Date();
  date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  return formatTime(date);
}

/**
 * Formats date in user's locale
 * Pass options to customize output (month, day, year, weekday, etc.)
 */
export function formatDateLocale(
  d?: Date | string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
): string {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, options);
}

/**
 * Formats a time range in user's locale
 * Example: "2:30 PM – 3:00 PM" or "14:30 – 15:00"
 */
export function formatTimeRange(
  start: Date | string,
  end: Date | string
): string {
  return `${formatTime(start)} – ${formatTime(end)}`;
}

export function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}
